// The Build HTTP client (Flows 4-6; packages/shared/src/http/build.ts BUILD_ROUTES -
// LANDED per the tech-lead's canonical route list, docs/phase-0/slice5-ux-flows.md:
// 250-265 "D2, now final"). Every response parses through the shared BuildResult/
// WorkflowValidation Zod schema apps/api validates against (app-engineering §2 "parse,
// don't validate"). `BuildStepView` never carries a `runnerClass` or cost figure
// (model/build.ts:122-134, D27/§0#6) - this client renders only what the server sends.
//
// ADR-0022 (Slice 5 close): the production-readiness read (`getProductionReadiness` ->
// `GET /build/production`), the per-item checklist patch (`updateChecklistItem` ->
// `POST /build/production/checklist/:key`), and the two hold-resolution step-actions
// (`confirmHardGateStep`/`verifyManualStep` -> `POST /build/:buildId/steps/:stepId/
// {confirm-hard-gate,verify-manual-step}`) that a prior draft of this file PROPOSED are
// DEFERRED to Slice 18, coupled to real build execution - `verify-manual-step` is a
// forged-completion-evidence attack surface (D66 invariants 14/15) that must ship WITH
// its Slice-18 hardening, never as a throwaway stub ahead of it (ADR-0022 decision 1).
// They are removed here rather than left as dead calls nothing invokes: `ProductionBuild.tsx`
// and `MvpBuild.tsx`'s hold states now render an honest "available with real build
// execution" affordance instead (ADR-0022 decision 2). Also note (ADR-0022 decision 3,
// for Slice 18): `GET /build/production` is shadowed by the registered `GET
// /build/:buildId` param route (resolves with `buildId="production"`) - the real route
// needs a distinct path or an explicit order carve-out.
import {
  BuildRequest,
  BuildResult,
  WorkflowValidation,
} from "@nightcrew/shared";
import type { AppId, Cloud, Result } from "@nightcrew/shared";
import { apiFetch, apiPost } from "./client.js";
import type { ClientError } from "./errors.js";

/** GET /v1/apps/:appId/build/workflow-validation - reads the current walkthrough
 * item list/state (D35). */
export const getWorkflowValidation = (
  appId: AppId,
): Promise<Result<WorkflowValidation, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/build/workflow-validation`, WorkflowValidation);

/** POST /v1/apps/:appId/build/workflow-validation - submits the founder's confirm/
 * adjust decisions across the app's own workflows (D35). The client never invents
 * `validatedAt` itself - only the server attests completion - so it always sends
 * `null` and reads the server's own value back from the response. */
export const submitWorkflowValidation = (
  appId: AppId,
  steps: WorkflowValidation["steps"],
): Promise<Result<WorkflowValidation, ClientError>> =>
  apiPost(`/v1/apps/${appId}/build/workflow-validation`, WorkflowValidation, {
    steps,
    validatedAt: null,
  });

/** POST /v1/apps/:appId/build - starts an MVP or Production build at the founder's
 * chosen cloud (D34), returning a `BuildResult` (a hard gate | manual step | an
 * entitlement refusal | the in-progress `building` projection). */
export const startBuild = (
  appId: AppId,
  stage: "mvp" | "production",
  cloud: Cloud,
): Promise<Result<BuildResult, ClientError>> =>
  apiPost(`/v1/apps/${appId}/build`, BuildResult, {
    appId,
    stage,
    cloud,
  } satisfies BuildRequest);

/** GET /v1/apps/:appId/build/:buildId - polls one build's per-step progress. */
export const getBuildStatus = (
  appId: AppId,
  buildId: string,
): Promise<Result<BuildResult, ClientError>> =>
  apiFetch(`/v1/apps/${appId}/build/${encodeURIComponent(buildId)}`, BuildResult);

/** POST /v1/apps/:appId/build/promote - the default-deny production promote (D34). */
export const promoteBuild = (appId: AppId, cloud: Cloud): Promise<Result<BuildResult, ClientError>> =>
  apiPost(`/v1/apps/${appId}/build/promote`, BuildResult, { cloud });
